import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core';
import { UpdateVariableBenefitConfigurationSubmission } from '../../../../../../models/submissions/update-variable-benefit-configuration-submission';
import { VariableBenefitDestinationJurisdictionDetail } from '../../../../../../models/submissions/variable-benefit-destination-jurisdiction-detail';
import { StatusPill } from '../../../../../../models/shared/status-pill';
import { DateConversionUtils } from '../../../../../../utils/date-conversion-utils';
import {
  currentVbPlanYear,
  vbPlanDateFromEffectiveDate
} from '../../../variable-benefit-accounts/utils/vb-annual-details-utils';

interface MinAgeRow {
  jurisdictionName: string;
  personName: string;
  isSpouse: boolean;
  age: number;
  dateOfBirth: string;
  isNew: boolean;
}

@Component({
  selector: 'app-update-vb-config-min-age-review-card',
  templateUrl: './update-vb-config-min-age-review-card.component.html',
  styleUrls: ['./update-vb-config-min-age-review-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UpdateVbConfigMinAgeReviewCardComponent implements OnChanges {
  @Input({ required: true }) submission!: UpdateVariableBenefitConfigurationSubmission;

  public minAgeRows: MinAgeRow[] = [];
  public newStatusPill = new StatusPill(0, $localize`New`, '#0D5D4B', '#E8F3F1');

  public ngOnChanges(): void {
    const previousDetails = this.submission?.previous?.destinationJurisdictions ?? [];
    const newDetails = this.submission?.new?.destinationJurisdictions ?? [];
    const previousById = new Map(previousDetails.map(d => [d.governingJurisdictionId, d]));
    const newById = new Map(newDetails.map(d => [d.governingJurisdictionId, d]));
    const allIds = new Set<number | null>([...previousById.keys(), ...newById.keys()]);
    const planYear =
      vbPlanDateFromEffectiveDate(this.submission?.new?.effectiveDate ?? null)?.planYear ?? currentVbPlanYear();

    this.minAgeRows = Array.from(allIds)
      .map(id => {
        const previous = previousById.get(id) ?? null;
        const next = newById.get(id) ?? null;
        const detail = next ?? previous!;
        const source = next?.minimumAgeDateOfBirth ? next : previous ?? detail;
        const jurisdictionName = detail.governingJurisdiction?.name ?? previous?.governingJurisdiction?.name ?? '';
        return this.toMinAgeRow(detail, source, jurisdictionName, previous === null, planYear);
      })
      .filter(row => !!row.dateOfBirth)
      .sort((a, b) => {
        if (!a.jurisdictionName) return 1;
        if (!b.jurisdictionName) return -1;
        return a.jurisdictionName.localeCompare(b.jurisdictionName);
      });
  }

  private toMinAgeRow(
    detail: VariableBenefitDestinationJurisdictionDetail,
    source: VariableBenefitDestinationJurisdictionDetail,
    jurisdictionName: string,
    isNew: boolean,
    planYear: number
  ): MinAgeRow {
    return {
      jurisdictionName,
      personName: source.minimumAgeName,
      isSpouse: source.minimumAgeSpouseId !== null,
      age: source.minimumAgeDateOfBirth
        ? DateConversionUtils.calculateAgeOnJanuaryFirst(source.minimumAgeDateOfBirth, planYear)
        : 0,
      dateOfBirth: source.minimumAgeDateOfBirth,
      isNew
    };
  }
}
